import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { TextFormat, Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './product-process-history.reducer';

export const ProductProcessHistory = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(
    overrideSortStateWithQueryParams(getSortState(pageLocation, 'id'), pageLocation.search),
  );

  const productProcessHistoryList = useAppSelector(state => state.productProcessHistory.entities);
  const loading = useAppSelector(state => state.productProcessHistory.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="product-process-history-heading" data-cy="ProductProcessHistoryHeading">
        <Translate contentKey="crawlerBackendApp.productProcessHistory.home.title">Product Process Histories</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="crawlerBackendApp.productProcessHistory.home.refreshListLabel">
              Refresh List
            </Translate>
          </Button>
          <Link
            to="/product-process-history/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="crawlerBackendApp.productProcessHistory.home.createLabel">
              Create new Product Process History
            </Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {productProcessHistoryList && productProcessHistoryList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('processType')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.processType">Process Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('processType')} />
                </th>
                <th className="hand" onClick={sort('stepType')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.stepType">Step Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('stepType')} />
                </th>
                <th className="hand" onClick={sort('stepStatus')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.stepStatus">Step Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('stepStatus')} />
                </th>
                <th className="hand" onClick={sort('retryCount')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.retryCount">Retry Count</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('retryCount')} />
                </th>
                <th className="hand" onClick={sort('payloadIn')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.payloadIn">Payload In</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('payloadIn')} />
                </th>
                <th className="hand" onClick={sort('payloadOut')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.payloadOut">Payload Out</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('payloadOut')} />
                </th>
                <th className="hand" onClick={sort('coolingOffEnd')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.coolingOffEnd">
                    Cooling Off End
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('coolingOffEnd')} />
                </th>
                <th className="hand" onClick={sort('offerExpirationDate')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.offerExpirationDate">
                    Offer Expiration Date
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('offerExpirationDate')} />
                </th>
                <th className="hand" onClick={sort('mandateSignedDate')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.mandateSignedDate">
                    Mandate Signed Date
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('mandateSignedDate')} />
                </th>
                <th className="hand" onClick={sort('desiredDate')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.desiredDate">Desired Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('desiredDate')} />
                </th>
                <th className="hand" onClick={sort('actualStartDate')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.actualStartDate">
                    Actual Start Date
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('actualStartDate')} />
                </th>
                <th className="hand" onClick={sort('transactionId')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.transactionId">
                    Transaction Id
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('transactionId')} />
                </th>
                <th className="hand" onClick={sort('waiveCoolingOffRight')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.waiveCoolingOffRight">
                    Waive Cooling Off Right
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('waiveCoolingOffRight')} />
                </th>
                <th className="hand" onClick={sort('depositRequired')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.depositRequired">
                    Deposit Required
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('depositRequired')} />
                </th>
                <th className="hand" onClick={sort('deposit')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.deposit">Deposit</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('deposit')} />
                </th>
                <th className="hand" onClick={sort('createdAt')}>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.createdAt">Created At</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdAt')} />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.productProcessHistory.productProcess">
                    Product Process
                  </Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {productProcessHistoryList.map((productProcessHistory, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button
                      tag={Link}
                      to={`/product-process-history/${productProcessHistory.id}`}
                      color="link"
                      size="sm"
                    >
                      {productProcessHistory.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.ProcessType.${productProcessHistory.processType}`} />
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.StepType.${productProcessHistory.stepType}`} />
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.StepStatus.${productProcessHistory.stepStatus}`} />
                  </td>
                  <td>{productProcessHistory.retryCount}</td>
                  <td>{productProcessHistory.payloadIn}</td>
                  <td>{productProcessHistory.payloadOut}</td>
                  <td>
                    {productProcessHistory.coolingOffEnd ? (
                      <TextFormat
                        type="date"
                        value={productProcessHistory.coolingOffEnd}
                        format={APP_LOCAL_DATE_FORMAT}
                      />
                    ) : null}
                  </td>
                  <td>
                    {productProcessHistory.offerExpirationDate ? (
                      <TextFormat
                        type="date"
                        value={productProcessHistory.offerExpirationDate}
                        format={APP_LOCAL_DATE_FORMAT}
                      />
                    ) : null}
                  </td>
                  <td>
                    {productProcessHistory.mandateSignedDate ? (
                      <TextFormat
                        type="date"
                        value={productProcessHistory.mandateSignedDate}
                        format={APP_LOCAL_DATE_FORMAT}
                      />
                    ) : null}
                  </td>
                  <td>
                    {productProcessHistory.desiredDate ? (
                      <TextFormat
                        type="date"
                        value={productProcessHistory.desiredDate}
                        format={APP_LOCAL_DATE_FORMAT}
                      />
                    ) : null}
                  </td>
                  <td>
                    {productProcessHistory.actualStartDate ? (
                      <TextFormat
                        type="date"
                        value={productProcessHistory.actualStartDate}
                        format={APP_LOCAL_DATE_FORMAT}
                      />
                    ) : null}
                  </td>
                  <td>{productProcessHistory.transactionId}</td>
                  <td>{productProcessHistory.waiveCoolingOffRight ? 'true' : 'false'}</td>
                  <td>{productProcessHistory.depositRequired ? 'true' : 'false'}</td>
                  <td>{productProcessHistory.deposit ? 'true' : 'false'}</td>
                  <td>
                    {productProcessHistory.createdAt ? (
                      <TextFormat type="date" value={productProcessHistory.createdAt} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {productProcessHistory.productProcess ? (
                      <Link to={`/product-process/${productProcessHistory.productProcess.id}`}>
                        {productProcessHistory.productProcess.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/product-process-history/${productProcessHistory.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/product-process-history/${productProcessHistory.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/product-process-history/${productProcessHistory.id}/delete`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="crawlerBackendApp.productProcessHistory.home.notFound">
                No Product Process Histories found
              </Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ProductProcessHistory;
